import React, { type FC, useId } from 'react'
import { Box, InputAdornment, type SxProps, type Theme, Typography } from '@mui/material'
import { Search } from '@mui/icons-material'

import { Translation } from '../../../../domain'

import { TextField } from '../../common'
import { Button, DownloadIcon } from '..'
import { FormattedTranslation, useTranslator } from '../../Internationalization'
import { ACTION_ORDER, defaultIcons, defaultTranslations, DESTRUCTIVE_ACTION } from './Actions'
import {
    useAreThereSelected,
    useClearSelection,
    useFilters,
    useIsSearching,
    useOnDownloadCSV,
    useRequiredSelection,
    useSearch,
    useSearchCharactersLeft,
    useSearchDispatcher,
    useSearchThreshold,
    useSelected,
    useShowsLoadedRows,
    useUtilities,
} from './State'
import type { ActionName, Line } from './Types'

const TOOLBAR_HEIGHT = 44
const HAIRLINE_HEIGHT = 20

const toolbarStyle: SxProps<Theme> = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, minHeight: TOOLBAR_HEIGHT, my: 3 }
const groupStyle: SxProps<Theme> = { display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'nowrap' }
const pushedRightStyle: SxProps<Theme> = { ...groupStyle, ml: 'auto' }
const hairlineStyle: SxProps<Theme> = { flex: 'none', width: '1px', height: HAIRLINE_HEIGHT, mr: 1, bgcolor: 'grey.200' }
const selectedCountStyle: SxProps<Theme> = { fontWeight: 'fontWeightMedium', color: 'grey.900', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }
const searchIconStyle: SxProps<Theme> = { mr: 2 }
/** Laid over the input's end, so showing or hiding it never resizes the field */
const charactersLeftStyle: SxProps<Theme> = {
    position: 'absolute',
    right: ({ spacing }) => spacing(2),
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
}

const SelectionActionButton = <L extends Line>({ action }: Readonly<{ action: ActionName }>): ReturnType<FC> => {
    const {
        icons: { [action]: Icon = defaultIcons[action] },
        labels: { [action]: label = defaultTranslations[action] },
        onSelect,
    } = useRequiredSelection<L>()
    const lines = useSelected<L>()
    const loaded = useShowsLoadedRows()
    const { [action]: onClick } = onSelect(lines)

    if (!onClick) {
        return null
    }

    return (
        <Button
            variant={action === DESTRUCTIVE_ACTION ? 'dangerOutline' : 'secondary'}
            size="medium"
            startIcon={<Icon />}
            /** The actions' dialogs only find the selected records in loaded rows */
            disabled={!loaded}
            onClick={() => onClick(lines)}
        >
            <FormattedTranslation id={label} />
        </Button>
    )
}

/** Added to the bar while rows are selected, instead of replacing the bar: search, filters and utilities stay usable */
const SelectionGroup = <L extends Line>(): ReturnType<FC> => {
    const count = useSelected<L>().length
    const clear = useClearSelection()

    return (
        <Box data-testid="table-selection" sx={pushedRightStyle}>
            <Box sx={hairlineStyle} aria-hidden />
            <Typography role="status" variant="body2" sx={selectedCountStyle}>
                <FormattedTranslation id={Translation.SELECTED_COUNT} values={{ count }} />
            </Typography>
            {ACTION_ORDER.map((action) => <SelectionActionButton<L> key={action} action={action} />)}
            <Button variant="ghost" size="medium" onClick={clear}>
                <FormattedTranslation id={Translation.CLEAR} />
            </Button>
        </Box>
    )
}

/**
 * Tells how many more characters a search needs to run.
 * It is a polite live region, rendered even while empty, so screen readers announce the count as it changes while typing.
 */
const SearchCharactersLeft: FC<Readonly<{ id: string, count: number }>> = ({ id, count }) => (
    <InputAdornment position="end" sx={charactersLeftStyle}>
        <Typography
            id={id}
            data-testid="table-search-characters-left"
            component="span"
            variant="caption"
            color="grey.500"
            aria-live="polite"
            aria-atomic="true"
        >
            {count > 0 && <FormattedTranslation id={Translation.SEARCH_CHARACTERS_LEFT} values={{ count }} />}
        </Typography>
    </InputAdornment>
)

const DownloadCSVButton: FC<Readonly<{ onClick: VoidFunction }>> = ({ onClick }) => (
    <Button variant="secondary" size="medium" startIcon={<DownloadIcon />} onClick={onClick}>
        <FormattedTranslation id={Translation.DOWNLOAD_CSV} />
    </Button>
)

export const Toolbar = <L extends Line>(): ReturnType<FC> => {
    const translator = useTranslator()

    const filters = useFilters()
    const utilities = useUtilities()
    const onDownloadCSV = useOnDownloadCSV()
    const hasSelection = useAreThereSelected<L>()

    const isSearching = useIsSearching()
    const searchText = useSearch()
    const dispatchSearch = useSearchDispatcher()
    const searchThreshold = useSearchThreshold()
    const charactersLeftId = useId()
    const charactersLeft = useSearchCharactersLeft()

    const hasUtilities = Boolean(onDownloadCSV) || Boolean(utilities)

    if (!isSearching && !filters && !hasUtilities && !hasSelection) {
        return null
    }

    return (
        <Box data-testid="table-toolbar" sx={toolbarStyle}>
            {isSearching && (
                <TextField
                    data-testid="table-search-input"
                    size="small"
                    placeholder={translator.formatTranslation(Translation.SEARCH_PLACEHOLDER, { count: searchThreshold })}
                    startIcon={<Search sx={searchIconStyle} />}
                    value={searchText}
                    onChange={({ target }): void => dispatchSearch(target.value)}
                    slotProps={searchThreshold > 0
                        ? {
                            input: { endAdornment: <SearchCharactersLeft id={charactersLeftId} count={charactersLeft} /> },
                            htmlInput: { 'aria-describedby': charactersLeftId },
                        }
                        : undefined}
                />
            )}
            {filters && <Box sx={groupStyle}>{filters}</Box>}
            {hasSelection && <SelectionGroup<L> />}
            {hasUtilities && (
                <Box sx={hasSelection ? groupStyle : pushedRightStyle}>
                    {onDownloadCSV ? <DownloadCSVButton onClick={onDownloadCSV} /> : null}
                    {utilities}
                </Box>
            )}
        </Box>
    )
}
